// src/store/api/PromocodeAnalyticsApi.ts
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
} from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';

// Types for real-time promocode analytics
export interface PromocodeAnalyticsData {
  promocodeId: string;
  promocodeName: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  maxUses?: number;
  currentUses: number;
  usageCount: number;
  totalDiscountAmount: number;
  usageRate?: number;
  categoriesUsed: Record<string, {
    categoryId: string;
    categoryName: string;
    usageCount: number;
    totalDiscountAmount: number;
  }>;
  isActive: boolean;
}

export interface PromocodePerformanceData {
  promocodeId: string;
  promocodeName: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  maxUses?: number;
  currentUses: number;
  usageCount: number;
  totalDiscountAmount: number;
  usageRate?: number | null;
}

export interface PromocodeStatsResult {
  totalPromocodes: number;
  activePromocodes: number;
  totalUsage: number;
  totalDiscounts: number;
  promocodes: PromocodeAnalyticsData[];
}

export const PromocodeAnalyticsApi = createApi({
  reducerPath: 'PromocodeAnalyticsApi',
  baseQuery: fakeBaseQuery<string>(),
  tagTypes: ['PromocodeStats'],
  endpoints: (builder) => ({
    // Get real-time promocode analytics for an event
    getPromocodeAnalytics: builder.query<PromocodeStatsResult, string>({
      queryFn: async (eventId) => {
        try {
          console.log(`Calculating promocode analytics for event: ${eventId}`);

          // Get event data first
          const eventDoc = await getDoc(doc(DB, 'events', eventId));
          if (!eventDoc.exists()) {
            return { error: 'Event not found' };
          }

          const eventData = eventDoc.data();
          const eventPromocodes = eventData.promoCodes || [];

          // Get ticket categories for reference
          const ticketCategoriesSnapshot = await getDocs(collection(DB, 'events', eventId, 'tickets'));
          const ticketCategories: Record<string, any> = {};
          ticketCategoriesSnapshot.docs.forEach((doc) => {
            ticketCategories[doc.id] = { id: doc.id, ...doc.data() };
          });

          // Get all confirmed reservations for this event
          const reservationsQuery = query(
            collection(DB, 'reservations'),
            where('eventid', '==', eventId),
            where('status', 'in', ['confirmed', 'validated'])
          );

          const reservationsSnapshot = await getDocs(reservationsQuery);
          console.log(`Found ${reservationsSnapshot.size} reservations for event ${eventId}`);

          // Initialize promocode analytics
          const promocodeAnalytics: Record<string, PromocodeAnalyticsData> = {};

          // Initialize all promocodes from event
          eventPromocodes.forEach((promo: any) => {
            promocodeAnalytics[promo.code] = {
              promocodeId: promo.code,
              promocodeName: promo.code,
              discountType: promo.mode === 'rate' ? 'percentage' : 'fixed',
              discountValue: promo.discount,
              maxUses: promo.maxSoldTicket,
              currentUses: 0,
              usageCount: 0,
              totalDiscountAmount: 0,
              usageRate: promo.maxSoldTicket ? 0 : undefined,
              categoriesUsed: {},
              isActive: true,
            };
          });

          // Process reservations and calculate usage
          for (const reservationDoc of reservationsSnapshot.docs) {
            const reservationData = reservationDoc.data();
            const promocodeInfo = reservationData.promocode;

            if (promocodeInfo?.promoCodeValue) {
              const promocodeValue = promocodeInfo.promoCodeValue;

              // Get tickets for this reservation
              const ticketsSnapshot = await getDocs(
                collection(DB, 'reservations', reservationDoc.id, 'tickets')
              );

              // Find the promocode configuration
              const eventPromocode = eventPromocodes.find((promo: any) => promo.code === promocodeValue);

              if (eventPromocode && eventPromocode.tickets) {
                // Initialize promocode if not exists (for edge cases)
                if (!promocodeAnalytics[promocodeValue]) {
                  promocodeAnalytics[promocodeValue] = {
                    promocodeId: promocodeValue,
                    promocodeName: promocodeValue,
                    discountType: eventPromocode.mode === 'rate' ? 'percentage' : 'fixed',
                    discountValue: eventPromocode.discount,
                    maxUses: eventPromocode.maxSoldTicket,
                    currentUses: 0,
                    usageCount: 0,
                    totalDiscountAmount: 0,
                    usageRate: eventPromocode.maxSoldTicket ? 0 : undefined,
                    categoriesUsed: {},
                    isActive: true,
                  };
                }

                const promoData = promocodeAnalytics[promocodeValue];
                promoData.currentUses += 1; // One usage per reservation

                // Process tickets that are eligible for this promocode
                ticketsSnapshot.docs.forEach((ticketDoc) => {
                  const ticketData = ticketDoc.data();
                  const categoryId = ticketData.categoryid;

                  // Check if this ticket category is eligible for the promocode
                  const isTicketEligible = eventPromocode.tickets.some(
                    (ticket: any) => ticket.id === ticketData.categoryid
                  );

                  if (isTicketEligible) {
                    promoData.usageCount += 1;

                    // Calculate discount amount
                    const originalPrice = ticketCategories[categoryId]?.price || 0;
                    const finalPrice = ticketData.price || 0;
                    let discountAmount = 0;

                    if (eventPromocode.mode === 'rate') {
                      discountAmount = (originalPrice * eventPromocode.discount) / 100;
                    } else {
                      discountAmount = Math.min(eventPromocode.discount, originalPrice);
                    }

                    promoData.totalDiscountAmount += discountAmount;

                    // Track category usage
                    if (!promoData.categoriesUsed[categoryId]) {
                      promoData.categoriesUsed[categoryId] = {
                        categoryId: categoryId,
                        categoryName: ticketCategories[categoryId]?.name || 'Unknown Category',
                        usageCount: 0,
                        totalDiscountAmount: 0,
                      };
                    }

                    promoData.categoriesUsed[categoryId].usageCount += 1;
                    promoData.categoriesUsed[categoryId].totalDiscountAmount += discountAmount;
                  }
                });

                // Update usage rate
                if (promoData.maxUses) {
                  promoData.usageRate = (promoData.currentUses / promoData.maxUses) * 100;
                }
              }
            }
          }

          // Calculate summary stats
          const promocodesArray = Object.values(promocodeAnalytics);
          const activePromocodes = promocodesArray.filter(p => p.usageCount > 0);
          const totalUsage = promocodesArray.reduce((sum, p) => sum + p.usageCount, 0);
          const totalDiscounts = promocodesArray.reduce((sum, p) => sum + p.totalDiscountAmount, 0);

          const result: PromocodeStatsResult = {
            totalPromocodes: promocodesArray.length,
            activePromocodes: activePromocodes.length,
            totalUsage,
            totalDiscounts,
            promocodes: promocodesArray,
          };

          return { data: result };
        } catch (error: any) {
          console.error('Error calculating promocode analytics:', error);
          return { error: error.message || 'Failed to calculate promocode analytics' };
        }
      },
      providesTags: (result, error, eventId) => [{ type: 'PromocodeStats', id: eventId }],
    }),

    // Get simplified promocode performance data (for backward compatibility)
    getPromocodePerformance: builder.query<PromocodePerformanceData[], string>({
      queryFn: async (eventId) => {
        try {
          console.log(`Calculating promocode performance for event: ${eventId}`);

          // Get event data first
          const eventDoc = await getDoc(doc(DB, 'events', eventId));
          if (!eventDoc.exists()) {
            return { error: 'Event not found' };
          }

          const eventData = eventDoc.data();
          const eventPromocodes = eventData.promoCodes || [];

          // Get ticket categories for reference
          const ticketCategoriesSnapshot = await getDocs(collection(DB, 'events', eventId, 'tickets'));
          const ticketCategories: Record<string, any> = {};
          ticketCategoriesSnapshot.docs.forEach((doc) => {
            ticketCategories[doc.id] = { id: doc.id, ...doc.data() };
          });

          // Get all confirmed reservations for this event
          const reservationsQuery = query(
            collection(DB, 'reservations'),
            where('eventid', '==', eventId),
            where('status', 'in', ['confirmed', 'validated'])
          );

          const reservationsSnapshot = await getDocs(reservationsQuery);

          // Initialize promocode analytics
          const promocodeAnalytics: Record<string, PromocodeAnalyticsData> = {};

          // Initialize all promocodes from event
          eventPromocodes.forEach((promo: any) => {
            promocodeAnalytics[promo.code] = {
              promocodeId: promo.code,
              promocodeName: promo.code,
              discountType: promo.mode === 'rate' ? 'percentage' : 'fixed',
              discountValue: promo.discount,
              maxUses: promo.maxSoldTicket,
              currentUses: 0,
              usageCount: 0,
              totalDiscountAmount: 0,
              usageRate: promo.maxSoldTicket ? 0 : undefined,
              categoriesUsed: {},
              isActive: true,
            };
          });

          // Process reservations and calculate usage
          for (const reservationDoc of reservationsSnapshot.docs) {
            const reservationData = reservationDoc.data();
            const promocodeInfo = reservationData.promocode;

            if (promocodeInfo?.promoCodeValue) {
              const promocodeValue = promocodeInfo.promoCodeValue;

              // Get tickets for this reservation
              const ticketsSnapshot = await getDocs(
                collection(DB, 'reservations', reservationDoc.id, 'tickets')
              );

              // Find the promocode configuration
              const eventPromocode = eventPromocodes.find((promo: any) => promo.code === promocodeValue);

              if (eventPromocode && eventPromocode.tickets) {
                // Initialize promocode if not exists (for edge cases)
                if (!promocodeAnalytics[promocodeValue]) {
                  promocodeAnalytics[promocodeValue] = {
                    promocodeId: promocodeValue,
                    promocodeName: promocodeValue,
                    discountType: eventPromocode.mode === 'rate' ? 'percentage' : 'fixed',
                    discountValue: eventPromocode.discount,
                    maxUses: eventPromocode.maxSoldTicket,
                    currentUses: 0,
                    usageCount: 0,
                    totalDiscountAmount: 0,
                    usageRate: eventPromocode.maxSoldTicket ? 0 : undefined,
                    categoriesUsed: {},
                    isActive: true,
                  };
                }

                const promoData = promocodeAnalytics[promocodeValue];
                promoData.currentUses += 1;

                // Process tickets that are eligible for this promocode
                ticketsSnapshot.docs.forEach((ticketDoc) => {
                  const ticketData = ticketDoc.data();
                  const categoryId = ticketData.categoryid;

                  // Check if this ticket category is eligible for the promocode
                  const isTicketEligible = eventPromocode.tickets.some(
                    (ticket: any) => ticket.id === ticketData.categoryid
                  );

                  if (isTicketEligible) {
                    promoData.usageCount += 1;

                    // Calculate discount amount
                    const originalPrice = ticketCategories[categoryId]?.price || 0;
                    let discountAmount = 0;

                    if (eventPromocode.mode === 'rate') {
                      discountAmount = (originalPrice * eventPromocode.discount) / 100;
                    } else {
                      discountAmount = Math.min(eventPromocode.discount, originalPrice);
                    }

                    promoData.totalDiscountAmount += discountAmount;
                  }
                });

                // Update usage rate
                if (promoData.maxUses) {
                  promoData.usageRate = (promoData.currentUses / promoData.maxUses) * 100;
                }
              }
            }
          }

          const performanceData: PromocodePerformanceData[] = Object.values(promocodeAnalytics).map(promo => ({
            promocodeId: promo.promocodeId,
            promocodeName: promo.promocodeName,
            discountType: promo.discountType,
            discountValue: promo.discountValue,
            maxUses: promo.maxUses,
            currentUses: promo.currentUses,
            usageCount: promo.usageCount,
            totalDiscountAmount: promo.totalDiscountAmount,
            usageRate: promo.usageRate || null,
          }));

          return { data: performanceData };
        } catch (error: any) {
          console.error('Error getting promocode performance:', error);
          return { error: error.message || 'Failed to get promocode performance' };
        }
      },
      providesTags: (result, error, eventId) => [{ type: 'PromocodeStats', id: `${eventId}-performance` }],
    }),

    // Invalidate promocode stats (useful when event data changes)
    invalidatePromocodeStats: builder.mutation<void, string>({
      queryFn: async (eventId) => 
        // This is just a trigger to invalidate cache
         ({ data: undefined })
      ,
      invalidatesTags: (result, error, eventId) => [
        { type: 'PromocodeStats', id: eventId },
        { type: 'PromocodeStats', id: `${eventId}-performance` },
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetPromocodeAnalyticsQuery,
  useGetPromocodePerformanceQuery,
  useInvalidatePromocodeStatsMutation,
} = PromocodeAnalyticsApi;